import React from 'react';
import { Box, Link, Typography, Alert } from '@mui/material';
import map from 'lodash/map';
import debounce from 'lodash/debounce';
import { Fields } from '@pv/common/components';
import { gql, useMutation, useQuery } from '@apollo/client';
import { SectionContainer } from '../events/EventDrawer/components';
import {
  CustomFieldValuesQuery,
  CustomFieldValuesQueryVariables,
  UpdateCustomFieldValuesMutation,
  UpdateCustomFieldValuesMutationVariables,
  VenueCustomFieldsQuery,
  VenueCustomFieldsQueryVariables,
} from '@pv/common/graphql/operations';
import { ErrorBoundary } from 'react-error-boundary';
import { error } from 'console';

const venueCustomFieldsQuery = gql`
  query VenueCustomFields($id: ID!) {
    venue(id: $id) {
      id
      customFields {
        id
        name
        userFieldKey
        index
        dataType
        options
        required
        description
      }
    }
  }
`;

const customFieldValuesQuery = gql`
  query CustomFieldValues($id: ID!) {
    event(id: $id) {
      id
      customFieldValues
      canUpdate {
        value
      }
      venue {
        id
      }
    }
  }
`;

const updateCustomFieldValuesMutation = gql`
  mutation UpdateCustomFieldValues($input: UpdateEventInput!) {
    updateEvent(input: $input) {
      event {
        id
        customFieldValues
      }
    }
  }
`;

const CustomFieldArea = ({ eventId }: { eventId: string }) => {
  const { data: eventData } = useQuery<
    CustomFieldValuesQuery,
    CustomFieldValuesQueryVariables
  >(customFieldValuesQuery, {
    variables: { id: eventId },
  });

  const [localValues, setLocalValues] = React.useState(
    eventData?.event?.customFieldValues || {}
  );

  React.useEffect(() => {
    if (eventData?.event?.customFieldValues) {
      setLocalValues(eventData.event.customFieldValues);
    }
  }, [eventData]);

  const canUpdateEvent = eventData?.event?.canUpdate?.value;

  const { data } = useQuery<
    VenueCustomFieldsQuery,
    VenueCustomFieldsQueryVariables
  >(venueCustomFieldsQuery, {
    variables: {
      id: eventData?.event?.venue?.id || '',
    },
    skip: !eventData?.event?.venue?.id,
  });

  const [updateCustomFieldValues] = useMutation<
    UpdateCustomFieldValuesMutation,
    UpdateCustomFieldValuesMutationVariables
  >(updateCustomFieldValuesMutation);

  let customFields = data?.venue?.customFields;

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedUpdate = React.useCallback(
    debounce((name: string, value: string, currentValues: any) => {
      const updatedValues = { ...currentValues, [name]: value };
      updateCustomFieldValues({
        variables: { input: { customFieldValues: updatedValues, id: eventId } },
        optimisticResponse: {
          updateEvent: {
            __typename: 'UpdateEventPayload',
            event: {
              __typename: 'Event',
              id: eventId,
              customFieldValues: updatedValues,
            },
          },
        },
      });
    }, 500),
    [updateCustomFieldValues, eventId]
  );

  const handleChange = (name: string, value: string) => {
    setLocalValues((prevValues: any) => ({ ...prevValues, [name]: value }));
    debouncedUpdate(name, value, { ...localValues, [name]: value });
  };

  const Fallback = ({ ufd }: { ufd: any }) => {
    return (
      <Box>
        <Alert severity="error">
          You have a duplicate custom field called {ufd.name} which cannot be
          displayed. Please update the custom field in your{' '}
          <Link href={'/settings/contact_form'}>Contact Form Settings</Link> to
          give it a unique name.
        </Alert>
      </Box>
    );
  };

  return (
    <SectionContainer>
      {map(customFields, (ufd) => {
        return (
          <Box key={ufd.id}>
            <ErrorBoundary
              fallbackRender={({ error }) => <Fallback ufd={ufd} />}
            >
              <Fields.PvCustomField
                label={ufd.name}
                variant="outlined"
                userFieldDefinition={ufd}
                value={localValues[ufd.name] || ''}
                onChange={(value: any) => handleChange(ufd.name, value)}
                disabled={!canUpdateEvent}
                helperText={ufd.description}
              />
            </ErrorBoundary>
          </Box>
        );
      })}
    </SectionContainer>
  );
};

export default CustomFieldArea;
